import { apply, burst, celebrate, onceToday, toast } from '../core/effects';
import { S } from '../core/game';
import { bus } from '../core/bus';
import { m } from '../i18n';
import { npcName, objName } from '../i18n/labels';
import { sfx } from '../audio/sfx';
import { sayEnglish } from '../audio/speech';
import { say, type Choice } from '../ui/dialog';
import { h } from '../ui/dom';
import { openTowerView, playTrainRide, type Route } from '../ui/tripScenes';
import { openMinigame } from '../minigames/registry';
import { ARRIVALS, MAPS, areaAt } from '../world/map';
import { px, type MapId, type ObjectId } from '../world/mapdef';
import type { World } from '../world/world';
import { isVehicle, riding } from './places';
import { payOrCard } from './bank';
import { logOrder, stamp } from './passport';
import { endDay } from './dayCycle';

/**
 * Day trips by train: buy a return ticket in English, ride, explore, ride home.
 * Taipei (101, MRT, Palace Museum) and Hualien (Taroko Gorge, Qixingtan Beach).
 */
type Dest = 'taipei' | 'hualien' | 'kaohsiung' | 'tainan';
const DESTS: Dest[] = ['taipei', 'hualien', 'kaohsiung', 'tainan'];
const PRICE: Record<Dest, number> = { taipei: 60, hualien: 90, kaohsiung: 150, tainan: 130 };
const RIDE_MINUTES: Record<Dest, number> = { taipei: 60, hualien: 120, kaohsiung: 90, tainan: 80 };
const LAST_TRAIN = 20 * 60;
/** Where the train pulls in: Taipei Main Station, Hualien Station (in the city map), the HSR stations in the south. */
const TRAIN_MAP: Record<Dest, MapId> = { taipei: 'taipei', hualien: 'hlcity', kaohsiung: 'kaohsiung', tainan: 'tainan' };
const TICKET_FLAG: Record<Dest, string> = { taipei: 'ticketTaipei', hualien: 'ticketHualien', kaohsiung: 'ticketKaohsiung', tainan: 'ticketTainan' };
/** The object that takes you home from each destination. */
const HOME_OBJECT: Record<Dest, ObjectId> = { taipei: 'tpTrain', hualien: 'hlTrain', kaohsiung: 'kh_hsr', tainan: 'tn_hsr' };
const EN_NAME: Record<Dest, string> = { taipei: 'Taipei', hualien: 'Hualien', kaohsiung: 'Kaohsiung', tainan: 'Tainan' };
/** Destination labels and hints: the originals live in trips.station, the HSR ones in the airport package. */
const destLabel = (d: Dest): string => (d === 'kaohsiung' || d === 'tainan' ? m().airport.hsr[d] : m().trips.station.dest[d]);
const destHint = (d: Dest): string => (d === 'kaohsiung' ? m().airport.hsr.hintKaohsiung : d === 'tainan' ? m().airport.hsr.hintTainan : m().trips.station.destHint[d]);
const STATION_ARRIVAL = { x: px(10), y: px(8) + 8, facing: 'up' as const };

let world: World | null = null;
export function bindTripsWorld(w: World): void {
  world = w;
}

const shuffle = <T>(a: T[]): T[] => a.map((v) => [Math.random(), v] as const).sort((x, y) => x[0] - y[0]).map((p) => p[1]);
const pick = <T>(a: readonly T[]): T => a[Math.floor(Math.random() * a.length)];
const ticket = (): Dest | null => DESTS.find((d) => S().daily[TICKET_FLAG[d]]) ?? null;

// --- Station -------------------------------------------------------------------------------

function buyTicket(): void {
  const t = m().trips.station;
  const who = npcName('stationClerk');
  if (S().time >= LAST_TRAIN) return say({ who, icon: '🚉', text: t.tooLate });
  const have = ticket();
  if (have) return say({ who, icon: '🎫', text: t.haveTicket(destLabel(have)) });
  sayEnglish(t.counter, 0.9);
  say({
    who,
    icon: '🎫',
    text: t.counter,
    choices: [
      ...DESTS.map((d): Choice => ({ text: `${d === 'kaohsiung' || d === 'tainan' ? '🚄' : '🚆'} ${destLabel(d)} — ${destHint(d)}`, cost: t.price(PRICE[d]), run: () => askTicket(d) })),
      { text: t.notNow },
    ],
  });
}

function askTicket(d: Dest): void {
  const t = m().trips.station;
  const who = npcName('stationClerk');
  // English names on purpose: this is the sentence you practise.
  const place = EN_NAME[d];
  const [ok, ...bad] = t.ask.map((x) => x.replace('{place}', place));
  say({
    who,
    icon: '🎫',
    text: t.howAsk,
    choices: shuffle([ok, ...bad]).map((o) => ({
      text: `🗣️ ${o}`,
      run: () => {
        sayEnglish(o, 0.9);
        if (o !== ok) {
          sfx('bad');
          return say({ who, icon: '🎫', text: t.wrong, choices: [{ text: m().common.back, run: () => askTicket(d) }] });
        }
        payOrCard(PRICE[d], who, '🎫', () => {
          S().daily[TICKET_FLAG[d]] = true;
          logOrder();
          sfx('ok');
          say({ who, icon: '🎫', text: t.bought(destLabel(d)) });
        }, () => askTicket(d));
      },
    })),
  });
}

const EN_PLACE: Record<Route, string> = { toTaipei: 'Taipei', toHualien: 'Hualien', home: 'Lantern Town' };

function ride(route: Route, dest: MapId, place: string, minutes: number, arrive: { x: number; y: number; facing: 'up' | 'down' | 'left' | 'right' }, custom?: { lines: string[]; en: string }): void {
  const r = m().trips.ride;
  const lines = custom?.lines ?? (route === 'toTaipei' ? r.toTaipei : route === 'toHualien' ? r.toHualien : r.home);
  sfx('door');
  // The announcement is English practice, so the place name stays in English too.
  playTrainRide(route, lines, r.announce(custom?.en ?? EN_PLACE[route]), r.skip, () => {
    apply({ minutes }, { float: false });
    world?.travel(dest, arrive);
    window.setTimeout(() => toast(r.arrive(place)), 900);
  });
}

function boardTrain(): void {
  const t = m().trips.station;
  const d = ticket();
  if (!d) return say({ who: objName('stTrain'), icon: '🚆', text: t.noTicket });
  if (S().time >= LAST_TRAIN + 60) return say({ who: objName('stTrain'), icon: '🚆', text: t.tooLate });
  const hsr = d === 'kaohsiung' || d === 'tainan';
  say({
    who: objName('stTrain'),
    icon: hsr ? '🚄' : '🚆',
    text: t.trainText(destLabel(d)),
    choices: [
      {
        text: t.board,
        cost: m().common.minutes(RIDE_MINUTES[d]),
        run: () => ride(d === 'hualien' ? 'toHualien' : 'toTaipei', TRAIN_MAP[d], destLabel(d), RIDE_MINUTES[d], ARRIVALS[TRAIN_MAP[d]]!, hsr ? { lines: m().airport.hsr.toSouth, en: EN_NAME[d] } : undefined),
      },
      { text: t.notNow },
    ],
  });
}

function trainHome(from: Dest): void {
  const t = m().trips.station;
  say({
    who: objName(HOME_OBJECT[from]),
    icon: from === 'kaohsiung' || from === 'tainan' ? '🚄' : '🚆',
    text: t.backText,
    choices: [
      {
        text: t.boardBack,
        cost: m().common.minutes(RIDE_MINUTES[from]),
        run: () => {
          if (riding.on === 'bike') riding.on = null;
          // The return half is used up: the round trip is over.
          const s = S();
          for (const d of DESTS) s.daily[TICKET_FLAG[d]] = false;
          ride('home', 'station', m().trips.mapNames.station, RIDE_MINUTES[from], STATION_ARRIVAL);
        },
      },
      { text: t.notNow },
    ],
  });
}

// --- Getting around: MRT in Taipei, the tourist shuttle in Hualien --------------------------------

/** Ride the MRT to a Taipei district (arrives at the map's `mrt` spot, or its ARRIVALS point). */
export function mrtTo(to: MapId): void {
  const t = m().trips.mrt;
  sfx('ring');
  toast(t.riding);
  stamp('mrt');
  apply({ minutes: 15 }, { float: false });
  const at = MAPS[to].spots?.mrt ?? ARRIVALS[to]!;
  world?.travel(to, at);
}

/** The MRT line menu: pick a stop (district MRT entrances call this too). */
export function mrtMenu(): void {
  const t = m().trips.mrt;
  const here = world?.map.id;
  const stops: [MapId, string][] = [
    ['taipei', t.toTower],
    ['museum', t.toMuseum],
    ['ximen', t.toXimen],
    ['longshan', t.toLongshan],
    ['cks', t.toCks],
    ['beitou', t.toBeitou],
  ];
  say({
    who: objName('mrtRide'),
    icon: '🚇',
    text: t.platform,
    choices: [
      ...stops.filter(([id]) => id !== here).map(([id, label]): Choice => ({ text: label, run: () => mrtTo(id) })),
      { text: m().world3.market.look },
    ],
  });
}

/** Move within the trip maps (taxis and the like). */
export function travelTo(map: MapId, at: { x: number; y: number; facing: 'up' | 'down' | 'left' | 'right' }): void {
  world?.travel(map, at);
}

/** Go down into the MRT station (district exits call this). */
export function goMrt(): void {
  sfx('door');
  world?.travel('mrt', ARRIVALS.mrt!);
}

type ShuttleStop = 'city' | 'taroko' | 'qixingtan';
const SHUTTLE: Record<ShuttleStop, () => { map: MapId; at: { x: number; y: number; facing: 'up' | 'down' | 'left' | 'right' } }> = {
  city: () => ({ map: 'hlcity', at: MAPS.hlcity.spots!.shuttle }),
  taroko: () => ({ map: 'hualien', at: MAPS.hualien.spots!.taroko }),
  qixingtan: () => ({ map: 'hualien', at: MAPS.hualien.spots!.qixingtan }),
};

/** The Taiwan Tourist Shuttle stop (台灣好行): Hualien city ⇄ Taroko ⇄ Qixingtan. `from` is this stop. */
export function hualienShuttle(from: ShuttleStop): void {
  const t = m().trips.shuttle;
  sayEnglish(t.ask, 0.9);
  say({
    who: t.stop,
    icon: '🚌',
    text: `“${t.ask}”\n\n${t.where}`,
    choices: [
      ...(['city', 'taroko', 'qixingtan'] as ShuttleStop[])
        .filter((k) => k !== from)
        .map((k): Choice => ({
          text: t[k],
          cost: m().common.minutes(30),
          run: () => {
            if (riding.on === 'bike') riding.on = null;
            sfx('door');
            apply({ minutes: 30 }, { float: false });
            const dst = SHUTTLE[k]();
            world?.travel(dst.map, dst.at);
            window.setTimeout(() => toast(t.riding), 300);
          },
        })),
      { text: m().common.back },
    ],
  });
}

// --- Food -----------------------------------------------------------------------------------

export interface FoodSpec {
  vendor: string;
  ok: string;
  bad: string[];
  done: string;
  price: number;
}

/** Order in English: choose the polite sentence, then pay. */
export function order(id: ObjectId, icon: string, f: FoodSpec, fx: { energy?: number; mood?: number }, after?: () => void): void {
  const who = objName(id);
  const market = m().world3.market;
  sayEnglish(f.vendor, 0.9);
  say({
    who,
    icon,
    text: `“${f.vendor}”\n\n${market.orderPrompt}`,
    choices: [
      ...shuffle([f.ok, ...f.bad]).map(
        (o): Choice => ({
          text: `🗣️ ${o}`,
          // Same price tag on every option, so it doesn't give the right sentence away.
          cost: market.cost(f.price),
          run: () => {
            sayEnglish(o, 0.9);
            if (o !== f.ok) {
              sfx('bad');
              return say({ who, icon, text: pick(market.wrong), choices: [{ text: m().common.back, run: () => order(id, icon, f, fx, after) }] });
            }
            if (S().coins < f.price) return toast(market.poor);
            sfx('ok');
            burst('sparkle', 6);
            apply({ coins: -f.price, ...fx, minutes: 15 });
            logOrder();
            if (onceToday(`order.${id}`)) apply({ stars: 2 }, { float: false });
            after?.();
            say({ who, icon, text: f.done });
          },
        }),
      ),
      { text: market.look },
    ],
  });
}

// --- Learning moments ---------------------------------------------------------------------------

/** One multiple-choice question; the first answer in `a` is right. */
export function quiz(who: string, icon: string, text: string, q: string, a: readonly string[], right: string, wrong: string, onRight: () => void): void {
  sayEnglish(q, 0.85);
  say({
    who,
    icon,
    text: `${text}\n\n${q}`,
    choices: shuffle(a.map((t, k) => ({ t, ok: k === 0 }))).map((o) => ({
      text: o.t,
      run: () => {
        sayEnglish(o.t, 0.9);
        if (o.ok) {
          sfx('ok');
          onRight();
          say({ who, icon, text: right });
        } else {
          sfx('bad');
          say({ who, icon, text: wrong, choices: [{ text: m().common.back, run: () => quiz(who, icon, text, q, a, right, wrong, onRight) }] });
        }
      },
    })),
  });
}

const MUSEUM: readonly ('muCabbage' | 'muMeat' | 'muDing' | 'muRuware' | 'muScroll' | 'muCurio')[] = ['muCabbage', 'muMeat', 'muDing', 'muRuware', 'muScroll', 'muCurio'];

function exhibit(id: (typeof MUSEUM)[number]): void {
  const t = m().trips.museum;
  const it = t.items[id];
  sayEnglish(it.label, 0.85);
  quiz(objName(id), '🏛️', `${t.intro}\n\n“${it.label}”`, it.q, it.a, t.right, t.wrong, () => {
    const s = S();
    if (!s.forever[`museum.${id}`]) {
      s.forever[`museum.${id}`] = true;
      apply({ stars: 2, study: 2 });
    }
    const seen = MUSEUM.filter((k) => s.forever[`museum.${k}`]).length;
    toast(t.seen(seen, MUSEUM.length));
    if (seen >= 4) stamp('museum');
  });
}

function mrtRules(i = 0): void {
  const t = m().trips.mrt;
  const r = t.rulesQs[i];
  if (!r) {
    if (onceToday('mrtRules')) apply({ stars: 3, study: 2 });
    celebrate();
    return say({ who: objName('mrtRules'), icon: '📋', text: t.rulesDone });
  }
  sayEnglish(r.q, 0.85);
  say({
    who: objName('mrtRules'),
    icon: '📋',
    text: i === 0 ? `${t.rules}\n\n${r.q}` : r.q,
    choices: shuffle(r.a.map((a, k) => ({ a, ok: k === 0 }))).map((o) => ({
      text: o.a,
      run: () => {
        if (o.ok) {
          sfx('ok');
          toast(t.rulesRight);
          mrtRules(i + 1);
        } else {
          sfx('bad');
          say({ who: objName('mrtRules'), icon: '📋', text: t.rulesWrong, choices: [{ text: m().common.back, run: () => mrtRules(i) }] });
        }
      },
    })),
  });
}

function towerView(): void {
  const t = m().trips.taipei;
  if (S().coins < 20) return toast(t.poor);
  apply({ coins: -20, minutes: 30 }, { float: false });
  sfx('fanfare');
  stamp('taipei101');
  S().forever['story.tower'] = true;
  const answer = h('div', { class: 'tv-quiz' });
  const close = openTowerView(t.viewTitle, [
    ...t.viewText.map((p) => h('p', null, p)),
    h('p', null, h('b', null, t.viewAsk)),
    h(
      'div',
      { class: 'chips' },
      shuffle(t.viewOptions.map((o, k) => ({ o, ok: k === 0 }))).map((x) =>
        h(
          'button',
          {
            class: 'chip',
            onclick: () => {
              sayEnglish(x.o, 0.9);
              answer.textContent = x.ok ? t.viewRight : t.viewWrong;
              if (x.ok) {
                sfx('ok');
                if (onceToday('towerQuiz')) apply({ stars: 3, study: 2 });
              } else sfx('bad');
            },
          },
          x.o,
        ),
      ),
    ),
    answer,
  ]);
  void close;
}

// --- Objects ----------------------------------------------------------------------------------

export const TRIP_OBJECTS: Partial<Record<ObjectId, () => void>> = {
  stTicket: buyTicket,
  stTrain: boardTrain,
  tpTrain: () => trainHome('taipei'),
  hlTrain: () => trainHome('hualien'),
  kh_hsr: () => trainHome('kaohsiung'),
  tn_hsr: () => trainHome('tainan'),
  twElevator() {
    const t = m().trips.taipei;
    say({ who: objName('twElevator'), icon: '🛗', text: t.elevator, choices: [{ text: t.goUp, run: towerView }, { text: m().common.back }] });
  },
  tpNoodles: () => order('tpNoodles', '🍜', m().trips.taipei.noodles, { energy: 30, mood: 8 }),
  tpCakes: () => order('tpCakes', '🍍', m().trips.taipei.cakes, { mood: 10 }, () => (S().affection.mom = Math.min(100, S().affection.mom + 4))),
  ubike() {
    const t = m().trips.taipei;
    const on = riding.on === 'bike';
    say({
      who: objName('ubike'),
      icon: '🚲',
      text: t.ubike,
      choices: [
        on
          ? { text: t.returnBike, run: () => ((riding.on = null), sfx('pop'), toast(t.returned)) }
          : {
              text: t.rent,
              run: () => {
                if (S().coins < 5) return toast(m().world3.market.poor);
                apply({ coins: -5 }, { float: false });
                riding.on = 'bike';
                sfx('ok');
                toast(t.ride);
              },
            },
        { text: m().common.back },
      ],
    });
  },
  mrtMap() {
    const t = m().trips.mrt;
    quiz(objName('mrtMap'), '🗺️', t.map, t.mapQuiz, t.mapOptions, t.mapRight, t.mapWrong, () => {
      if (onceToday('mrtMap')) apply({ stars: 2, study: 1 });
    });
  },
  mrtRules: () => mrtRules(0),
  mrtRide: () => mrtMenu(),
  muCabbage: () => exhibit('muCabbage'),
  muMeat: () => exhibit('muMeat'),
  muDing: () => exhibit('muDing'),
  muRuware: () => exhibit('muRuware'),
  muScroll: () => exhibit('muScroll'),
  muCurio: () => exhibit('muCurio'),
  muExit() {
    const t = m().trips.museum;
    say({
      who: objName('muExit'),
      icon: '🚇',
      text: t.exit,
      choices: [{ text: t.ride, run: () => (sfx('ring'), world?.travel('mrt', ARRIVALS.mrt!)) }, { text: m().common.back }],
    });
  },
  hlHelmet() {
    const t = m().trips.hualien;
    const s = S();
    if (s.daily.helmet) return say({ who: objName('hlHelmet'), icon: '⛑️', text: t.already });
    say({
      who: objName('hlHelmet'),
      icon: '⛑️',
      text: t.helmet,
      choices: [{ text: t.rent, run: () => ((s.daily.helmet = true), sfx('pop'), toast(t.wearing)) }, { text: m().common.back }],
    });
  },
  hlView() {
    const t = m().trips.hualien;
    if (!S().daily.helmet) return say({ who: objName('hlView'), icon: '⛑️', text: t.viewHelmet });
    say({
      who: objName('hlView'),
      icon: '🔭',
      text: t.view,
      choices: [
        {
          text: t.look,
          run: () => {
            sfx('flap');
            celebrate();
            if (onceToday('hlView')) apply({ mood: 15, stars: 4 });
            stamp('taroko');
            S().forever['story.gorge'] = true;
            say({ who: objName('hlView'), icon: '🐦', text: t.looked });
          },
        },
        { text: m().common.back },
      ],
    });
  },
  hlStones() {
    const t = m().trips.hualien;
    say({ who: objName('hlStones'), icon: '🪨', text: t.stones, choices: [{ text: t.stack, run: () => (stamp('beach'), openMinigame('stoneStack' as Parameters<typeof openMinigame>[0])) }, { text: m().common.back }] });
  },
  hlSea() {
    const t = m().trips.hualien;
    say({
      who: objName('hlSea'),
      icon: '🌊',
      text: t.sea,
      choices: [
        {
          text: t.listen,
          run: () => {
            sfx('water');
            if (onceToday('hlSea')) apply({ mood: 15, energy: 5 });
            stamp('beach');
            toast(t.listened);
          },
        },
        { text: m().common.back },
      ],
    });
  },
  hlMochi: () => order('hlMochi', '🍡', m().trips.hualien.mochi, { energy: 12, mood: 12 }),
  hlPancake: () => order('hlPancake', '🫓', m().trips.hualien.pancake, { energy: 22, mood: 8 }),
  hlWonton: () => order('hlWonton', '🥟', m().trips.hualien.wonton, { energy: 25, mood: 6 }),
  hlQingshui() {
    const t = m().trips.hualien;
    say({
      who: objName('hlQingshui'),
      icon: '🏔️',
      text: t.qingshui,
      choices: [
        { text: t.lookCliff, run: () => (sfx('water'), onceToday('hlQingshui') && apply({ mood: 12, stars: 3 }), stamp('beach'), say({ who: objName('hlQingshui'), icon: '🌊', text: t.lookedCliff })) },
        { text: m().common.back },
      ],
    });
  },
  hlNineTurns() {
    const t = m().trips.hualien;
    if (!S().daily.helmet) return say({ who: objName('hlNineTurns'), icon: '⛑️', text: t.viewHelmet });
    if (onceToday('hlNineTurns')) apply({ mood: 8, stars: 2 });
    stamp('taroko');
    say({ who: objName('hlNineTurns'), icon: '🪧', text: t.nineTurns });
  },
  hlAmis() {
    const t = m().trips.hualien;
    say({
      who: objName('hlAmis'),
      icon: '💃',
      text: t.amis,
      choices: [
        { text: t.watchDance, run: () => (sfx('fanfare'), burst('heart', 6), onceToday('hlAmis') && apply({ mood: 18, stars: 3 }), say({ who: objName('hlAmis'), icon: '💃', text: t.danced })) },
        { text: m().common.back },
      ],
    });
  },
  hlWhale: whaleBoat,
  htDesk: hotelDesk,
  htBed() {
    const t = m().trips.hualien.hotel;
    const s = S();
    if (!s.forever[`hotel.${s.dayIndex}`]) return say({ who: objName('htBed'), icon: '🛏️', text: t.noRoom });
    say({ who: objName('htBed'), icon: '🛏️', text: t.bed, choices: [{ text: t.sleep, run: () => endDay() }, { text: m().common.back }] });
  },
};

const WHALE_PRICE = 40;
function whaleBoat(): void {
  const t = m().trips.hualien;
  say({
    who: objName('hlWhale'),
    icon: '🐋',
    text: t.whale,
    choices: [
      {
        text: t.boat(WHALE_PRICE),
        run: () => {
          if (S().coins < WHALE_PRICE) return toast(m().world3.market.poor);
          apply({ coins: -WHALE_PRICE, minutes: 60 });
          sfx('water');
          celebrate(true);
          if (onceToday('hlWhale')) apply({ mood: 20, stars: 5 });
          stamp('beach');
          sayEnglish('Dolphins are mammals. They breathe air, just like us.', 0.85);
          say({ who: npcName('captain'), icon: '🐬', text: `${pick(t.sightings)}\n\n${t.seaFact}` });
        },
      },
      { text: m().common.back },
    ],
  });
}

const HOTEL_PRICE = 120;
function hotelDesk(): void {
  const t = m().trips.hualien.hotel;
  const who = npcName('receptionist');
  const s = S();
  if (s.forever[`hotel.${s.dayIndex}`]) return say({ who, icon: '🔑', text: t.already });
  sayEnglish(t.desk, 0.9);
  say({
    who,
    icon: '🛎️',
    text: t.desk,
    choices: [
      {
        text: t.price(HOTEL_PRICE),
        run: () => {
          const [ok, ...bad] = t.ask;
          say({
            who,
            icon: '🛎️',
            text: t.howAsk,
            choices: shuffle([ok, ...bad]).map((o) => ({
              text: `🗣️ ${o}`,
              run: () => {
                sayEnglish(o, 0.9);
                if (o !== ok) return (sfx('bad'), say({ who, icon: '🛎️', text: t.wrong, choices: [{ text: m().common.back, run: hotelDesk }] }));
                if (S().coins < HOTEL_PRICE) return toast(t.poor);
                apply({ coins: -HOTEL_PRICE });
                S().forever[`hotel.${S().dayIndex}`] = true;
                logOrder();
                sfx('ok');
                say({ who, icon: '🔑', text: t.booked });
              },
            })),
          });
        },
      },
      { text: m().trips.station.notNow },
    ],
  });
}

/** People on the trips. Returns false if the id isn't one of them. */
export function talkTrips(id: string): boolean {
  const t = m().trips;
  switch (id) {
    case 'stationClerk':
      buyTicket();
      return true;
    case 'guide':
      say({ who: npcName('guide'), icon: '🧭', text: pick(t.taipei.guide) });
      return true;
    case 'busker':
      say({
        who: npcName('busker'),
        icon: '🎻',
        text: t.taipei.busker,
        choices: [{ text: t.taipei.listen, run: () => (sfx('fanfare'), onceToday('busker') && apply({ mood: 12 }), toast(t.taipei.listened)) }, { text: m().common.back }],
      });
      return true;
    case 'docent':
      say({ who: npcName('docent'), icon: '🏛️', text: pick(t.museum.docent) });
      return true;
    case 'ranger':
      say({ who: npcName('ranger'), icon: '⛑️', text: pick(t.hualien.ranger) });
      return true;
    case 'hiker':
      say({ who: npcName('hiker'), icon: '🥾', text: pick(t.hualien.hiker) });
      return true;
    case 'mochiAuntie':
      TRIP_OBJECTS.hlMochi!();
      return true;
    case 'captain':
      whaleBoat();
      return true;
    case 'receptionist':
      hotelDesk();
      return true;
    default:
      return false;
  }
}

let warnedAt = 0;
/** Per-frame rules for the trips: helmets on the trail, bikes only in Taipei. */
let lastSafe: { map: string; x: number; y: number } = { map: '', x: 0, y: 0 };

export function tripTick(w: World): void {
  // Rides stay outside: walking into a building returns them (see content/mobility.ts).
  if (isVehicle(riding.on) && w.map.indoor) {
    riding.on = null;
    toast(m().airport.rent.indoor);
  }
  // Without a helmet the gorge (area 'gorge' on the Taroko map) pushes you back to where you were.
  const inGorge = w.map.id === 'hualien' && areaAt(w.map, w.player.x, w.player.y) === 'gorge';
  if (!inGorge) lastSafe = { map: w.map.id, x: w.player.x, y: w.player.y };
  else if (!S().daily.helmet) {
    if (lastSafe.map === w.map.id) {
      w.player.x = lastSafe.x;
      w.player.y = lastSafe.y;
    }
    w.cancelMove();
    const now = performance.now();
    if (now - warnedAt > 2500) {
      warnedAt = now;
      sfx('bad');
      toast(m().trips.hualien.needHelmet);
    }
  }
}

/** First arrival in Hualien each day: a welcome. */
export function enteredTrip(id: string): void {
  if (id === 'hlcity' && onceToday('hlWelcome')) window.setTimeout(() => toast(m().trips.hualien.welcome), 1200);
}

// Waking up at the hotel after a night in Hualien.
bus.on('dayStart', () => {
  if (S().player.map === 'hotel') window.setTimeout(() => toast(m().trips.hualien.hotel.wake), 2800);
});
